import { useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@baluu/ui/components/button";
import { Textarea } from "@baluu/ui/components/textarea";
import { Spinner } from "@baluu/ui/components/spinner";
import { cn } from "@baluu/ui/lib/utils";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchoolSlug } from "../school";
import {
  CUSTOMER_NOTE_MAX_LENGTH,
  acceptOutcome,
  announcementApplies,
  changedParts,
  declineBody,
  errorMessageOf,
  formatDateTime,
  formatWhen,
  isPendingView,
  lineTotal,
  moneyMode,
  moneyRows,
  serverKeyOf,
  shouldRefetchAfterError,
  withEcho,
  withdrawnExplanation,
  type AcceptRequestProposalResponse,
  type ProposalOverride,
  type RequestProposalPayload,
  type RequestProposalPlace,
  type RequestProposalStatus,
} from "./request/requestProposal";

// /secure/request/<proposal-id>: the customer's answer to a school's
// proposed change to their booking request. A port of v1's
// RequestProposalClient: Accept (then on to the pay page when there's money
// due) or "This doesn't work for me" with an optional note; the accepted
// states by where the money stands; declined, expired and withdrawn.

const TITLE = "Proposed change to your request";

type Message = { text: string; key: string; echoed?: RequestProposalStatus };

export function RequestProposalView({ ctx, proposalId }: { ctx: ViewContext; proposalId: string }) {
  const noteId = useId();
  const noteCountId = useId();
  const base = `${ctx.api}/widget/pages/request-proposals/${encodeURIComponent(proposalId)}`;
  const page = useQuery({
    queryKey: ["booking", "request-proposal", ctx.api, proposalId],
    queryFn: () => bookingJson<RequestProposalPayload>(base),
    retry: (count, error) => !(error instanceof BookingApiError && error.status < 500) && count < 2,
  });

  // A local echo of an answer, set when its POST succeeds: it shows while
  // the server still reads PENDING (a refetch already in flight when the
  // answer went through lands with the old state), and the server's state
  // wins as soon as it has moved on (withEcho).
  const [echo, setEcho] = useState<ProposalOverride | null>(null);
  const [submitting, setSubmitting] = useState<"accept" | "decline" | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<Message | null>(null);
  const [announcement, setAnnouncement] = useState<Message | null>(null);
  // Synchronous in-flight guard: two clicks in one tick both read
  // `submitting` as null. Stays set once the page leaves for payment.
  const inFlight = useRef(false);

  if (page.isLoading) return <BookingLoading rows={4} />;
  if (page.isError) {
    const status = page.error instanceof BookingApiError ? page.error.status : 0;
    if (status === 404) return <RecordNotFound what="proposal" />;
    if (status === 429) return <BusyPanel />;
    return (
      <BookingProblem
        title="We couldn’t load this proposal"
        message={messageOf(page.error, "Please try again in a moment.")}
        onRetry={() => page.refetch()}
      />
    );
  }
  const data = page.data;
  if (!data || isOtherSchoolSlug(ctx.businessSlug, data.school?.slug)) return <RecordNotFound what="proposal" />;

  const key = serverKeyOf(data);
  const view: RequestProposalPayload = withEcho(data, echo);
  const schoolName = view.school.name || "the school";
  const busy = submitting !== null || leaving;
  const isPending = isPendingView(view);
  const tz = view.school.timezone;
  const changed = changedParts(view);
  const money = moneyMode(view);
  const respondBy = formatDateTime(view.expiresAt, tz);
  const tickets = view.lines.filter((l) => l.kind !== "addOn");
  const addOns = view.lines.filter((l) => l.kind === "addOn");
  const errorText = error && error.key === key ? error.text : null;
  const announcementText = announcement && announcementApplies(announcement, data) ? announcement.text : null;

  const reread = () => void page.refetch();

  async function accept() {
    if (inFlight.current) return;
    inFlight.current = true;
    let leftForPayment = false;
    setSubmitting("accept");
    setError(null);
    setAnnouncement(null);
    try {
      const result = await sendJson<AcceptRequestProposalResponse>(`${base}/accept`, "POST");
      const outcome = acceptOutcome(result);
      if (outcome.kind === "pay") {
        leftForPayment = true;
        setLeaving(true);
        setAnnouncement({ text: "Change accepted. Taking you to payment…", key });
        window.location.assign(ctx.links.site(outcome.paymentUrl));
        return;
      }
      setEcho(outcome.override);
      setAnnouncement({ text: outcome.announcement, key, echoed: "ACCEPTED" });
      reread();
    } catch (e) {
      setError({ text: errorMessageOf(e), key });
      if (shouldRefetchAfterError(e)) reread();
    } finally {
      if (!leftForPayment) inFlight.current = false;
      setSubmitting(null);
    }
  }

  async function decline() {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting("decline");
    setError(null);
    setAnnouncement(null);
    const body = declineBody(note);
    try {
      await sendJson(`${base}/decline`, "POST", body);
      setEcho({ status: "DECLINED", canRespond: false, customerNote: body.note ?? null });
      setDeclineOpen(false);
      setAnnouncement({ text: `Thanks — we’ve let ${schoolName} know.`, key, echoed: "DECLINED" });
      reread();
    } catch (e) {
      setError({ text: errorMessageOf(e), key });
      if (shouldRefetchAfterError(e)) reread();
    } finally {
      inFlight.current = false;
      setSubmitting(null);
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      {view.school.logoUrl && (
        <div className="flex justify-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={view.school.logoUrl} alt={schoolName} className="max-h-20 max-w-full object-contain" />
        </div>
      )}

      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{TITLE}</h1>
        <p className="text-sm text-muted-foreground">from {schoolName}</p>
      </header>

      {isPending && (
        <p className="text-sm">
          {view.customer.firstName ? `Hi ${view.customer.firstName}, ` : ""}
          {schoolName} has suggested a change to your booking request. Have a look and let them know whether it works for
          you.
        </p>
      )}

      <StateNotice view={view} schoolName={schoolName} payHref={view.paymentUrl ? ctx.links.site(view.paymentUrl) : null} />

      {view.partnerMessage && (
        <figure className="rounded-lg border-l-4 border-primary bg-card p-4">
          <figcaption className="mb-2 text-sm font-semibold text-muted-foreground">Message from {schoolName}</figcaption>
          <blockquote className="whitespace-pre-line text-sm">{view.partnerMessage}</blockquote>
        </figure>
      )}

      <section aria-labelledby="proposal-compare-heading" className="space-y-3">
        <h2 id="proposal-compare-heading" className="text-lg font-semibold">
          What’s changing
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <PlaceCard heading="You asked for" place={view.requested} when={formatWhen(view.requested, tz)} showListing={changed.listing} muted />
          <PlaceCard
            heading={`${schoolName} proposes`}
            place={view.proposed}
            when={formatWhen(view.proposed, tz)}
            showListing={changed.listing}
            highlight={changed}
          />
        </div>
      </section>

      {view.lines.length > 0 && (
        <section aria-labelledby="proposal-lines-heading" className="space-y-3">
          <h2 id="proposal-lines-heading" className="text-lg font-semibold">
            Your booking
          </h2>
          <LineList title={addOns.length ? "Tickets" : null} lines={tickets} currency={view.currency} />
          {addOns.length > 0 && <LineList title="Add-ons" lines={addOns} currency={view.currency} />}
        </section>
      )}

      {money && (
        <dl className="space-y-2 rounded-lg border bg-card p-4">
          {moneyRows(view, money === "total").map((row) => (
            <div key={row.term} className="flex justify-between gap-4 text-sm">
              <dt className={cn(row.strong && "font-semibold")}>{row.term}</dt>
              <dd className={cn("whitespace-nowrap", row.strong && "font-semibold")}>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {isPending && respondBy && (
        <p className="text-sm font-semibold">Please respond by {respondBy}.</p>
      )}

      {isPending && !declineOpen && (
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button onClick={accept} disabled={busy}>
            {(submitting === "accept" || leaving) && <Spinner className="mr-2 size-4" />}
            Accept
          </Button>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => {
              setError(null);
              setDeclineOpen(true);
            }}
          >
            This doesn’t work for me
          </Button>
        </div>
      )}

      {isPending && declineOpen && (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void decline();
          }}
        >
          <label htmlFor={noteId} className="font-medium">
            Anything you’d like {schoolName} to know? (optional)
          </label>
          <Textarea
            id={noteId}
            value={note}
            maxLength={CUSTOMER_NOTE_MAX_LENGTH}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            autoFocus
            disabled={busy}
            aria-describedby={noteCountId}
            placeholder="For example, dates or times that would suit you better"
          />
          <p id={noteCountId} className="text-right text-xs text-muted-foreground">
            {`${note.length}/${CUSTOMER_NOTE_MAX_LENGTH}`}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button type="submit" disabled={busy}>
              {submitting === "decline" && <Spinner className="mr-2 size-4" />}
              Let {schoolName} know
            </Button>
            <Button type="button" variant="outline" disabled={busy} onClick={() => setDeclineOpen(false)}>
              Back
            </Button>
          </div>
        </form>
      )}

      {view.status === "ACCEPTED" && view.paymentState === "processing" && (
        <Button onClick={accept} disabled={busy}>
          {(submitting === "accept" || leaving) && <Spinner className="mr-2 size-4" />}
          Try again
        </Button>
      )}

      {errorText && <Notice tone="error">{errorText}</Notice>}

      {/* Always mounted, so screen readers announce what lands in it. */}
      <div aria-live="polite" role="status">
        {announcementText && <p className="text-sm font-semibold">{announcementText}</p>}
      </div>
    </div>
  );
}

/** When the API asks the page to slow down (429). */
function BusyPanel() {
  return (
    <div className="mx-auto max-w-xl space-y-4">
      <h1 className="text-2xl font-semibold">{TITLE}</h1>
      <Notice tone="info">
        <strong>We’re a little busy right now.</strong> Please try again in a minute.
      </Notice>
    </div>
  );
}

function StateNotice({ view, schoolName, payHref }: { view: RequestProposalPayload; schoolName: string; payHref: string | null }) {
  switch (view.status) {
    case "PENDING":
      return null;
    case "ACCEPTED":
      return <AcceptedNotice view={view} schoolName={schoolName} payHref={payHref} />;
    case "DECLINED":
      return (
        <Notice tone="info">
          <p>
            <strong>You told {schoolName} this doesn’t work for you.</strong> Your original request is back with them.
          </p>
          {view.customerNote && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-muted-foreground">Your note</p>
              <blockquote className="whitespace-pre-line">{view.customerNote}</blockquote>
            </div>
          )}
        </Notice>
      );
    case "EXPIRED":
      return (
        <Notice tone="info">
          <strong>This proposal has expired.</strong> The time to respond ran out, so your booking request has been closed.
          Please contact {schoolName} if you’d still like to book.
        </Notice>
      );
    case "WITHDRAWN":
    default:
      return (
        <Notice tone="info">
          <strong>This proposal is no longer open.</strong> {withdrawnExplanation(view.withdrawnReason, schoolName)}
        </Notice>
      );
  }
}

function AcceptedNotice({ view, schoolName, payHref }: { view: RequestProposalPayload; schoolName: string; payHref: string | null }) {
  const payBy = formatDateTime(view.payByDate, view.school.timezone);
  switch (view.paymentState) {
    case "pay":
      return (
        <Notice tone="success">
          <p>
            <strong>You accepted the change.</strong> Complete your payment to secure your place
            {payBy ? <> — please pay by {payBy}</> : null}.
          </p>
          {payHref && (
            <Button asChild className="mt-4 w-full sm:w-auto">
              <a href={payHref}>Accepted — complete payment</a>
            </Button>
          )}
        </Notice>
      );
    case "nothing_to_pay":
      return (
        <Notice tone="success">
          <strong>You’re booked — nothing to pay now.</strong> You accepted the change and {schoolName} has your booking.
          We’ve emailed you the details.
        </Notice>
      );
    case "contact_school":
      return (
        <Notice tone="info">
          <strong>Your acceptance was received</strong>, but something went wrong setting up payment. Please contact{" "}
          {schoolName}.
        </Notice>
      );
    case "cancelled":
      return (
        <Notice tone="info">
          <strong>This booking is no longer active.</strong> Please contact {schoolName} if you have any questions.
        </Notice>
      );
    case "processing":
    default:
      return (
        <Notice tone="info">
          <strong>You accepted the change.</strong> We’re still confirming your booking — this usually takes a moment. If
          this message stays, use Try again below.
        </Notice>
      );
  }
}

function PlaceCard({
  heading,
  place,
  when,
  showListing,
  muted = false,
  highlight,
}: {
  heading: string;
  place: RequestProposalPlace;
  when: string | null;
  showListing: boolean;
  muted?: boolean;
  highlight?: { listing: boolean; when: boolean; location: boolean };
}) {
  const rows: Array<{ term: string; value: string; changed: boolean }> = [];
  if (showListing && place.listingTitle) rows.push({ term: "Listing", value: place.listingTitle, changed: !!highlight?.listing });
  rows.push({ term: "When", value: when ?? "To be confirmed", changed: !!highlight?.when });
  if (place.location) rows.push({ term: "Where", value: place.location, changed: !!highlight?.location });

  return (
    <div className={cn("rounded-lg border bg-card p-4", muted ? "text-muted-foreground" : "border-primary")}>
      <h3 className="mb-2 font-semibold">{heading}</h3>
      {!showListing && place.listingTitle && <p className="mb-2 text-sm">{place.listingTitle}</p>}
      <dl className="flex flex-col gap-2">
        {rows.map((row) => (
          <div key={row.term}>
            <dt className="text-xs text-muted-foreground">
              {row.term}
              {row.changed && <span className="ml-2 font-semibold text-primary">Changed</span>}
            </dt>
            <dd className={cn("text-sm", row.changed && "font-semibold")}>{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function LineList({ title, lines, currency }: { title: string | null; lines: RequestProposalPayload["lines"]; currency: string }) {
  if (lines.length === 0) return null;
  return (
    <div>
      {title && <h3 className="mb-1 text-xs font-semibold text-muted-foreground">{title}</h3>}
      <ul className="flex flex-col gap-1">
        {lines.map((line, i) => (
          <li key={`${line.label}-${i}`} className="flex justify-between gap-4 text-sm">
            <span>
              {line.quantity} × {line.label}
            </span>
            <span className="whitespace-nowrap">{lineTotal(line, currency)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
